import React, { useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaPhoneAlt, FaEnvelope, FaWhatsapp, FaInstagram,
  FaFacebook, FaTelegram, FaMapMarkerAlt, FaClock,
} from "react-icons/fa";
import { FiArrowRight, FiSend, FiCheckCircle } from "react-icons/fi";
import Helmet from "../components/Helmet/Helmet";
import { siteConfig } from "../utils/site";
import "../styles/contact.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const SUBJECTS = [
  "General Enquiry",
  "Custom Jewellery Design",
  "Gemstone Consultation",
  "Order Support",
  "Wholesale / Partnership",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setSent(true);
      setLoading(false);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    }, 900);
  };

  return (
    <>
      <Helmet
        title="Contact Roha Jewellery | Ethiopian Gemstone Experts"
        description="Contact Roha Jewellery in Addis Ababa for gemstone consultation, custom jewellery, showroom visits and order support."
        keywords="contact Roha Jewellery, Addis Ababa jewellery, Ethiopian gemstone consultation"
        canonical={`${siteConfig.url}/contact`}
      />

      <main className="roha-page contact-page">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="contact-hero">
          <div className="contact-hero__bg" aria-hidden="true" />
          <Container>
            <motion.div
              className="contact-hero__content"
              initial="hidden" animate="visible" variants={stagger}
            >
              <motion.p className="roha-eyebrow" variants={fadeUp}>
                Roha Jewellery · Addis Ababa
              </motion.p>
              <motion.h1 variants={fadeUp}>
                Let's create something<br />
                <span className="contact-hero__gold">extraordinary</span> together.
              </motion.h1>
              <motion.p className="contact-hero__sub" variants={fadeUp}>
                Whether you're seeking a bespoke engagement ring, exploring Ethiopian gemstones,
                or need expert guidance — our team is here for you.
              </motion.p>
              <motion.div className="contact-hero__actions" variants={fadeUp}>
                <a href="#contact-form" className="roha-button roha-button--solid">
                  Send a Message <FiArrowRight className="ms-2" />
                </a>
                <Link to="/design-request" className="roha-button roha-button--ghost">
                  Custom Design Request
                </Link>
              </motion.div>
            </motion.div>
          </Container>
        </section>

        {/* ── CONTACT CARDS ─────────────────────────────────────────────── */}
        <section className="roha-section">
          <Container>
            <motion.div
              className="roha-section__heading"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <p className="roha-eyebrow">Get in Touch</p>
              <h2>Choose how you'd like to reach us.</h2>
            </motion.div>

            <motion.div
              className="contact-methods-grid"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            >
              {/* Phone */}
              <motion.a
                href={`tel:${siteConfig.phones[0].replace(/\s+/g, "")}`}
                className="contact-method-card"
                variants={fadeUp}
                whileHover={{ y: -6 }}
              >
                <div className="contact-method-card__icon">
                  <FaPhoneAlt />
                </div>
                <h3>Call Us</h3>
                <strong>{siteConfig.phones[0]}</strong>
                {siteConfig.phones[1] && <span>{siteConfig.phones[1]}</span>}
                <p>Speak directly with our jewellery consultants</p>
                <div className="contact-method-card__arrow"><FiArrowRight /></div>
              </motion.a>

              {/* WhatsApp */}
              <motion.a
                href={`https://wa.me/${siteConfig.phones[0].replace(/\D/g, "")}`}
                className="contact-method-card contact-method-card--whatsapp"
                variants={fadeUp}
                whileHover={{ y: -6 }}
                target="_blank" rel="noopener noreferrer"
              >
                <div className="contact-method-card__icon">
                  <FaWhatsapp />
                </div>
                <h3>WhatsApp</h3>
                <strong>{siteConfig.phones[0]}</strong>
                <p>Quick responses for urgent enquiries</p>
                <div className="contact-method-card__arrow"><FiArrowRight /></div>
              </motion.a>

              {/* Email */}
              <motion.a
                href={`mailto:${siteConfig.email}`}
                className="contact-method-card"
                variants={fadeUp}
                whileHover={{ y: -6 }}
              >
                <div className="contact-method-card__icon">
                  <FaEnvelope />
                </div>
                <h3>Email</h3>
                <strong>{siteConfig.email}</strong>
                <p>Send detailed enquiries and custom requests</p>
                <div className="contact-method-card__arrow"><FiArrowRight /></div>
              </motion.a>

              {/* Visit */}
              <motion.div
                className="contact-method-card"
                variants={fadeUp}
                whileHover={{ y: -6 }}
              >
                <div className="contact-method-card__icon">
                  <FaMapMarkerAlt />
                </div>
                <h3>Visit Us</h3>
                <strong>Bole Dembel City Center</strong>
                <span>GFS013, Addis Ababa, Ethiopia</span>
                <p>
                  <FaClock style={{ marginRight: 6, opacity: 0.6 }} />
                  {siteConfig.hours}
                </p>
                <div className="contact-method-card__arrow"><FiArrowRight /></div>
              </motion.div>
            </motion.div>
          </Container>
        </section>

        {/* ── MAP + FORM ────────────────────────────────────────────────── */}
        <section className="roha-section roha-section--soft" id="contact-form">
          <Container>
            <Row className="g-5 align-items-start">

              {/* Left — Map + info */}
              <Col lg="5">
                <motion.div
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
                >
                  <motion.div variants={fadeUp}>
                    <p className="roha-eyebrow">Find Us</p>
                    <h2 style={{ marginBottom: "1.5rem" }}>
                      Roha Jewellery<br />Addis Ababa
                    </h2>
                  </motion.div>

                  {/* Map */}
                  <motion.div className="contact-map-frame" variants={fadeUp}>
                    <iframe
                      title="Roha Jewellery Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.8560365478215!2d38.85833807406636!3d8.985392189671984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b9b9c155a6be1%3A0x23cc903ef85e3fe9!2z4Yqk4YiNIOGMgiDhiq7hi63hiqsg4YiG4Y2VIOGJtOGKreGKkuGKq-GIjSDhibDhjI3hiaPhiKgg4Yql4Yu1IOGKpeGKkyDhjZXhiK7hjYzhiL3hipPhiI0g4Yqu4YiM4YyFIChMRyBLb2ljYSBIb3BlIFRlY2huaWNhbCBBcHBsaWNhdGlvbiBhbmQgUHJvZmVzc2lvbmFsIENvbGxlZ2Up!5e0!3m2!1sen!2set!4v1753941581266!5m2!1sen!2set"
                      width="100%" height="100%"
                      style={{ border: 0 }}
                      allowFullScreen="" loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </motion.div>

                  {/* Address + hours */}
                  <motion.div className="contact-info-pills" variants={fadeUp}>
                    <div className="contact-info-pill">
                      <FaMapMarkerAlt />
                      <span>{siteConfig.address}</span>
                    </div>
                    <div className="contact-info-pill">
                      <FaClock />
                      <span>{siteConfig.hours}</span>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeUp} style={{ marginTop: "1.5rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <a
                      href="https://maps.google.com/?q=Bole+Dembel+City+Center,Addis+Ababa,Ethiopia"
                      target="_blank" rel="noopener noreferrer"
                      className="roha-button roha-button--solid"
                    >
                      Get Directions
                    </a>
                    <a
                      href={`https://wa.me/${siteConfig.phones[0].replace(/\D/g, "")}`}
                      target="_blank" rel="noopener noreferrer"
                      className="roha-button roha-button--ghost"
                    >
                      <FaWhatsapp className="me-2" /> WhatsApp
                    </a>
                  </motion.div>

                  {/* Social */}
                  <motion.div className="contact-social" variants={fadeUp}>
                    <p className="contact-social__label">Follow Roha Jewellery</p>
                    <div className="contact-social__icons">
                      <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <FaInstagram />
                      </a>
                      <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                        <FaFacebook />
                      </a>
                      <a href="https://t.me" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                        <FaTelegram />
                      </a>
                    </div>
                  </motion.div>
                </motion.div>
              </Col>

              {/* Right — Form */}
              <Col lg="7">
                <motion.div
                  className="contact-form-card"
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                >
                  {sent ? (
                    <div className="contact-form-success">
                      <div className="contact-form-success__icon">
                        <FiCheckCircle size={48} />
                      </div>
                      <h3>Message Sent!</h3>
                      <p>
                        Thank you for reaching out to Roha Jewellery. Our team will respond
                        within 24 hours during business days.
                      </p>
                      <button
                        className="roha-button roha-button--ghost"
                        onClick={() => setSent(false)}
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="contact-form-card__header">
                        <p className="roha-eyebrow">Send a Message</p>
                        <h3>We'd love to hear from you.</h3>
                        <p className="contact-form-card__sub">
                          Fill out the form and we'll respond within 24 hours.
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="contact-form">
                        <Row className="g-3">
                          <Col md="6">
                            <div className="contact-field">
                              <label>Full Name <span>*</span></label>
                              <input
                                type="text" name="name" value={form.name}
                                onChange={handleChange} placeholder="Your full name"
                                required
                              />
                            </div>
                          </Col>
                          <Col md="6">
                            <div className="contact-field">
                              <label>Email Address <span>*</span></label>
                              <input
                                type="email" name="email" value={form.email}
                                onChange={handleChange} placeholder="your@email.com"
                                required
                              />
                            </div>
                          </Col>
                          <Col md="6">
                            <div className="contact-field">
                              <label>Phone / WhatsApp</label>
                              <input
                                type="tel" name="phone" value={form.phone}
                                onChange={handleChange} placeholder="+251 9XX XXX XXX"
                              />
                            </div>
                          </Col>
                          <Col md="6">
                            <div className="contact-field">
                              <label>Subject</label>
                              <select name="subject" value={form.subject} onChange={handleChange}>
                                <option value="">Select a subject</option>
                                {SUBJECTS.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          </Col>
                          <Col md="12">
                            <div className="contact-field">
                              <label>Message <span>*</span></label>
                              <textarea
                                name="message" value={form.message}
                                onChange={handleChange} rows={6}
                                placeholder="Tell us about your jewellery needs, design ideas, or any questions..."
                                required
                              />
                            </div>
                          </Col>
                        </Row>

                        <div className="contact-form__footer">
                          <button
                            type="submit"
                            className="roha-button roha-button--solid contact-submit-btn"
                            disabled={loading}
                          >
                            {loading ? (
                              <span className="contact-spinner" />
                            ) : (
                              <><FiSend className="me-2" /> Send Message</>
                            )}
                          </button>
                          <p className="contact-form__note">
                            We respond within 24 hours · Your data is safe with us
                          </p>
                        </div>
                      </form>
                    </>
                  )}
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ── SERVICES STRIP ───────────────────────────────────────────── */}
        <section className="roha-section">
          <Container>
            <motion.div
              className="roha-section__heading"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <p className="roha-eyebrow">Our Services</p>
              <h2>How we can help you.</h2>
            </motion.div>
            <motion.div
              className="contact-services-grid"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            >
              {[
                { icon: "💍", title: "Bespoke Design", desc: "Custom engagement rings and jewellery designed to your vision" },
                { icon: "💎", title: "Gemstone Sourcing", desc: "Ethically sourced Ethiopian opals, rubies, sapphires and more" },
                { icon: "🔍", title: "Gemstone Consultation", desc: "Expert guidance on selecting the perfect stone for any occasion" },
                { icon: "🛡️", title: "Certification", desc: "Certified authenticity documentation for all our gemstones" },
                { icon: "🔧", title: "Repair & Restoration", desc: "Professional jewellery repair and restoration services" },
                { icon: "🌍", title: "Worldwide Delivery", desc: "Secure insured shipping to over 50 countries globally" },
              ].map((s) => (
                <motion.div key={s.title} className="contact-service-card" variants={fadeUp}>
                  <span className="contact-service-card__icon">{s.icon}</span>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </section>

      </main>
    </>
  );
};

export default Contact;
